"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

type CookieConsentType = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};

type CookieConsentContextType = {
  consent: CookieConsentType;
  updateConsent: (type: "analytics" | "marketing", value: boolean) => void;
  acceptAll: () => void;
  essentialOnly: () => void;
  savePreferences: (analytics: boolean, marketing: boolean) => void;
  showConsentBanner: boolean;
  setShowConsentBanner: (show: boolean) => void;
  showPreferencesPanel: boolean;
  setShowPreferencesPanel: (show: boolean) => void;
};

const STORAGE_KEY = "cookieConsent";

const defaultConsent: CookieConsentType = {
  essential: true,
  analytics: false,
  marketing: false,
};

const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsent must be used within CookieConsentProvider",
    );
  }
  return context;
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsentType>(defaultConsent);
  const [showConsentBanner, setShowConsentBanner] = useState<boolean>(true);
  const [showPreferencesPanel, setShowPreferencesPanel] =
    useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState(false); // Prevent flash of banner

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedConsent: CookieConsentType = JSON.parse(saved);
        setConsent(parsedConsent);
        setShowConsentBanner(false); // User has already made a choice
      }
    } catch (e) {
      console.error("Failed to load cookie consent", e);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  const saveToStorage = (newConsent: CookieConsentType) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newConsent));
    } catch (e) {
      console.error("Failed to save cookie consent", e);
    }
  };

  const updateConsent = (type: "analytics" | "marketing", value: boolean) => {
    setConsent((prev) => ({ ...prev, [type]: value }));
  };

  const applyConsent = (current: CookieConsentType) => {
    if (current.analytics) {
      console.log("✅ Google Analytics → ENABLED");
      // TODO: Load GA script here
    } else {
      console.log("❌ Google Analytics → DISABLED");
    }

    if (current.marketing) {
      console.log("✅ Marketing tracking → ENABLED");
    } else {
      console.log("❌ Marketing tracking → DISABLED");
    }
  };

  const acceptAll = () => {
    const full = { essential: true, analytics: true, marketing: true };
    setConsent(full);
    setShowConsentBanner(false);
    setShowPreferencesPanel(false);
    saveToStorage(full);
    applyConsent(full);
  };

  const essentialOnly = () => {
    const essential = { essential: true, analytics: false, marketing: false };
    setConsent(essential);
    setShowConsentBanner(false);
    setShowPreferencesPanel(false);
    saveToStorage(essential);
    applyConsent(essential);
  };

  const savePreferences = (analytics: boolean, marketing: boolean) => {
    const newConsent = { essential: true, analytics, marketing };
    setConsent(newConsent);
    setShowConsentBanner(false);
    setShowPreferencesPanel(false);
    saveToStorage(newConsent);
    applyConsent(newConsent);
  };

  // Don't render anything until we checked localStorage (avoids flicker)
  if (!isLoaded) return null;

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        updateConsent,
        acceptAll,
        essentialOnly,
        savePreferences,
        showConsentBanner,
        setShowConsentBanner,
        showPreferencesPanel,
        setShowPreferencesPanel,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}
